import { useState } from "react";
import { Facebook, Linkedin, Link2, Mail, Check, Share2 } from "lucide-react";
import { absoluteUrl } from "@/lib/schema";
import { Button } from "@/components/ui/button";

interface ShareButtonsProps {
  path: string;
  title: string;
  /** Compact row for the article header; "full" adds a label. */
  variant?: "compact" | "full";
}

/**
 * Social sharing row. Uses plain share URLs (no third-party scripts) so the
 * article stays fast and privacy-friendly.
 */
export function ShareButtons({ path, title, variant = "compact" }: ShareButtonsProps) {
  const [copied, setCopied] = useState(false);
  const url = absoluteUrl(path);
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const links = [
    {
      label: "Share on Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      Icon: Facebook,
    },
    {
      label: "Share on X",
      href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      Icon: XIcon,
    },
    {
      label: "Share on LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      Icon: Linkedin,
    },
    {
      label: "Share by email",
      href: `mailto:?subject=${encodedTitle}&body=${encodedUrl}`,
      Icon: Mail,
    },
  ];

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {variant === "full" && (
        <span className="mr-1 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          <Share2 className="h-3.5 w-3.5" aria-hidden /> Share
        </span>
      )}
      {links.map(({ label, href, Icon }) => (
        <a
          key={label}
          href={href}
          target="_blank"
          rel="noreferrer noopener"
          aria-label={label}
          title={label}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card text-muted-foreground transition hover:border-primary/50 hover:text-primary"
        >
          <Icon className="h-4 w-4" aria-hidden />
        </a>
      ))}
      <Button
        type="button"
        onClick={copy}
        aria-label="Copy link to this article"
        title="Copy link"
        variant="outline"
        size="sm"
        className="rounded-full text-muted-foreground hover:text-primary"
      >
        {copied ? <Check className="h-4 w-4" aria-hidden /> : <Link2 className="h-4 w-4" aria-hidden />}
        {copied ? "Copied" : "Copy link"}
      </Button>
    </div>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}
